import React, { useState, useEffect, useMemo } from "react";
import { Login, Register } from "./components";
import DynamicSutraLoader from "./components/DynamicSutraLoader";
import { sutraComponents } from "./components/Sutra_code/sutraLazyComponents";
import Header from "./components/header";
import { useDispatch, useSelector } from "react-redux";
import { setQuestion, sutraMaster, sutraPoints } from "./Redux/actions";
import { fetchUserProgress, getSutraMasterArray } from "./components/dataUtils";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { auth } from "./firebase";
import GoogleUserModal from "./components/GoogleUserModal"; // Import your modal
import "./App.css";

// --- Extracted async logic for user progress ---
const handleUserProgress = async ({
  user,
  dispatch,
  sutramasterjsonfile,
  navigate,
  setLoggeduser,
  setLoading,
}) => {
  setLoggeduser(user.displayName || user.email || "");
  try {
    const progress = await fetchUserProgress(dispatch);
    if (progress) {
      const matchsutra = sutramasterjsonfile.findIndex(
        (arr) =>
          arr[0] === progress["attemptlevel"] &&
          arr[1] === progress["attemptchapter"] &&
          arr[2] === progress["attemptsutra"]
      );
      dispatch(sutraMaster(matchsutra));
      dispatch(setQuestion(progress["attemptquestion"]));
      if (progress["sutrapoints"]) {
        dispatch(sutraPoints(progress["sutrapoints"]));
      }
      navigate(
        `/${progress["attemptlevel"].toLowerCase()}/` +
          `${progress["attemptchapter"].toLowerCase()}/` +
          `${progress["attemptsutra"]}`
      );
    } else {
      dispatch(sutraMaster(0));
      dispatch(setQuestion(0));
      navigate("/level1/chapter1/sutra1_4_3");
    }
  } catch (error) {
    console.log("Error inside fetchUserProgress:", error);
  } finally {
    setLoading(false);
  }
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Redux state
  const sutranumber = useSelector((state) => state.sutraMaster);

  // App state
  const [loggeduser, setLoggeduser] = useState("");
  const [loading, setLoading] = useState(true);

  // Onboarding modal state
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [googleUserForModal, setGoogleUserForModal] = useState(null);

  // Sutra master array
  const sutramasterjsonfile = useMemo(() => getSutraMasterArray(), []);

  // ----------- AUTH REDIRECT LOGIC -----------
  useEffect(() => {
    const publicRoutes = ["/login", "/register"];
    const isPublicRoute = publicRoutes.includes(location.pathname);

    if (!auth.currentUser && !isPublicRoute) {
      navigate("/login", { replace: true });
    }
  }, [location, navigate]);
  // --------------------------------------------

  // ----------- FETCH USER DATA ON AUTH STATE CHANGE -----------
  useEffect(() => {
    setLoading(true);
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Check for user progress
        const progress = await fetchUserProgress(dispatch);
        if (!progress) {
          // No progress: trigger onboarding modal for Google users
          setGoogleUserForModal(user); // Pass the user object to the modal
          setShowGoogleModal(true);
          setLoading(false); // Stop loading, block main app until onboarding
        } else {
          // User is fully registered
          setShowGoogleModal(false);
          setGoogleUserForModal(null);
          handleUserProgress({
            user,
            dispatch,
            sutramasterjsonfile,
            navigate,
            setLoggeduser,
            setLoading,
          });
        }
      } else {
        setLoggeduser("");
        setLoading(false);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line
  }, [dispatch, navigate, sutramasterjsonfile]);
  // ------------------------------------------------------------

  const handleLogout = async () => {
    await auth.signOut();
    setLoggeduser("");
    dispatch(sutraMaster(0));
    dispatch(setQuestion(0));
    dispatch(sutraPoints(0));
    // Clear user-related localStorage for security
    localStorage.removeItem("authCode");
    localStorage.removeItem("name");
    localStorage.removeItem("userId");
    navigate("/login");
  };

  // Handler for closing the onboarding modal after successful registration
  const handleOnboardingComplete = async () => {
    setShowGoogleModal(false);
    setGoogleUserForModal(null);
    setLoading(true);
    // Fetch progress and continue as normal
    if (auth.currentUser) {
      await handleUserProgress({
        user: auth.currentUser,
        dispatch,
        sutramasterjsonfile,
        navigate,
        setLoggeduser,
        setLoading,
      });
    } else {
      setLoading(false);
    }
  };

  // Handler for closing the onboarding modal WITHOUT completing registration
  const handleOnboardingAbandon = async () => {
    await auth.signOut();
    setLoggeduser("");
    setShowGoogleModal(false);
    setGoogleUserForModal(null);
    // Clear user-related localStorage for security
    localStorage.removeItem("authCode");
    localStorage.removeItem("name");
    localStorage.removeItem("userId");
    navigate("/login");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "2rem" }}>Loading...</div>
    );
  }

  return (
    <div className="main-parent-container">
      {/* Onboarding modal for Google users (or any social login) */}
      {showGoogleModal && googleUserForModal && (
        <GoogleUserModal
          show={showGoogleModal}
          onHide={handleOnboardingAbandon} // <-- This ensures logout if modal is closed
          onSuccess={handleOnboardingComplete} // <-- Call this after successful registration
          googleNewUser={googleUserForModal}
          setloggeduser={setLoggeduser}
          dispatch={dispatch}
        />
      )}

      {/* Hide main app if onboarding is required */}
      {!showGoogleModal &&
        location.pathname !== "/login" &&
        location.pathname !== "/register" && (
          <Header
            loggeduser={loggeduser}
            userLogout={handleLogout}
            selectedGame={sutramasterjsonfile[sutranumber]?.[2] || "sutra1_4_3"}
            sutramasterjsonfile={sutramasterjsonfile}
          />
        )}

      <Routes>
        <Route
          path="/login"
          element={
            <Login setloggeduser={setLoggeduser} updateGames={() => {}} />
          }
        />
        <Route path="/register" element={<Register />} />
        <Route
          path="/:level/:chapter/:game"
          element={
            !showGoogleModal ? (
              <DynamicSutraLoader
                sutraComponents={sutraComponents}
                sutramasterjsonfile={sutramasterjsonfile}
              />
            ) : null
          }
        />
      </Routes>
    </div>
  );
}

export default App;
